import { useCallback, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const query = (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
  return new URLSearchParams(cleaned).toString();
};

const useAnalysisSnapshots = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const request = useCallback(
    async (url, params = {}, { silent404 = false } = {}) => {
      const qs = query(params);
      try {
        const response = await Utilities.fetchWithAuth("GET", `${url}${qs ? `?${qs}` : ""}`, null, navigate);
        if (response?.ok) return response.data;
        if (silent404 && response?.status === 404) return null;
        Utilities.showError(response, response?.data?.detail || response?.msg || "Request failed");
        return null;
      } catch (error) {
        console.error(error);
        message.error("Request failed");
        return null;
      }
    },
    [navigate],
  );

  const withLoading = useCallback(async (fn) => {
    setLoading(true);
    try {
      return await fn();
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSnapshots = useCallback(
    (params) => withLoading(async () => listFrom(await request(Settings.urls.academics.aggregationSnapshots, params))),
    [request, withLoading],
  );

  const fetchHeatmap = useCallback(
    (params) =>
      withLoading(async () =>
        listFrom(await request(Settings.urls.academics.snapshotHeatmap, params, { silent404: true })),
      ),
    [request, withLoading],
  );

  const fetchClassGroupSummary = useCallback(
    (params) =>
      withLoading(async () =>
        listFrom(await request(Settings.urls.academics.snapshotClassGroupSummary, params, { silent404: true })),
      ),
    [request, withLoading],
  );

  const fetchSchoolWide = useCallback(
    (params) => withLoading(() => request(Settings.urls.academics.snapshotSchoolWide, params, { silent404: true })),
    [request, withLoading],
  );

  const fetchStreamDrilldown = useCallback(
    (params) =>
      withLoading(async () => listFrom(await request(Settings.urls.academics.snapshotStreamDrilldown, params))),
    [request, withLoading],
  );

  const fetchStreamComparison = useCallback(
    (params) => withLoading(() => request(Settings.urls.academics.snapshotStreamComparison, params)),
    [request, withLoading],
  );

  const fetchTrend = useCallback(
    (params, { silentLoading = false } = {}) => {
      const run = async () =>
        listFrom(await request(Settings.urls.academics.snapshotTrend, params, { silent404: true }));
      return silentLoading ? run() : withLoading(run);
    },
    [request, withLoading],
  );

  const fetchMultiPeriodTrend = useCallback(
    (params, { silentLoading = false } = {}) => {
      const run = async () => {
        const trendParams = {
          scopeType: params.scopeType,
          classGroup: params.classGroup,
          subjectCategory: params.subjectCategory,
        };

        const fetchSingle = async (termID, acadyrID) => {
          const data = await request(
            Settings.urls.academics.snapshotTrend,
            { ...trendParams, termID, acadyrID },
            { silent404: true },
          );
          const snapshots = Array.isArray(data) ? data.filter(Boolean) : [];
          if (!snapshots.length) return null;
          return {
            termName: snapshots[0].termName,
            academicYear: snapshots[0].academicYear,
            rows: snapshots.map((snap) => ({
              scopeType: snap.scopeType,
              scopeLabel: snap.scopeLabel,
              subjectName: snap.subjectName,
              subjectCategory: snap.subjectCategory,
              classGroup: snap.classGroup,
              termName: snap.termName,
              academicYear: snap.academicYear,
              studentCount: snap.studentCount,
              avgScore: snap.avgScore,
              medianScore: snap.medianScore,
              highestScore: snap.highestScore,
              lowestScore: snap.lowestScore,
              stdDeviation: snap.stdDeviation,
            })),
          };
        };

        const periodPairs = String(params.periods || "")
          .split(",")
          .filter(Boolean);

        const parsePair = (pair) => {
          const [t, y] = String(pair).split(":");
          const termID = Number(t);
          const acadyrID = Number(y);
          if (!Number.isFinite(termID) || !Number.isFinite(acadyrID)) return null;
          return { termID, acadyrID, key: pair };
        };

        const validPeriods = periodPairs.map(parsePair).filter(Boolean);

        if (validPeriods.length === 0) return null;

        if (validPeriods.length === 1) {
          const { termID, acadyrID } = validPeriods[0];
          const single = await fetchSingle(termID, acadyrID);
          if (!single) return null;
          return {
            periods: [{ periodOrder: 0, termName: single.termName, academicYear: single.academicYear }],
            rows: single.rows.map((r) => ({ ...r, periodOrder: 0, avgScoreDelta: null })),
            missing: [],
          };
        }

        // Prefer the backend multi-period computation.
        const multi = await request(Settings.urls.academics.snapshotTrend, params, { silent404: true });
        if (multi && Array.isArray(multi.periods)) {
          return { ...multi, missing: [] };
        }

        // Fallback: fetch each period individually and skip any with no data.
        const fetched = [];
        const missing = [];
        for (const { termID, acadyrID, key } of validPeriods) {
          const single = await fetchSingle(termID, acadyrID);
          if (!single) {
            missing.push(key);
            continue;
          }
          fetched.push(single);
        }
        if (!fetched.length) return null;

        const periods = fetched.map((f, idx) => ({
          periodOrder: idx,
          termName: f.termName,
          academicYear: f.academicYear,
        }));
        const prevByCat = {};
        const rows = [];
        fetched.forEach((f, idx) => {
          f.rows.forEach((r) => {
            const key = `${r.scopeLabel}|${r.subjectCategory}`;
            const prev = prevByCat[key];
            const delta = prev != null && r.avgScore != null ? Number(r.avgScore) - Number(prev) : null;
            prevByCat[key] = r.avgScore != null ? Number(r.avgScore) : null;
            rows.push({ ...r, periodOrder: idx, avgScoreDelta: delta });
          });
        });
        return { periods, rows, missing };
      };
      return silentLoading ? run() : withLoading(run);
    },
    [request, withLoading],
  );

  const fetchClassSubjects = useCallback(
    async () => listFrom(await request(Settings.urls.academics.class_subjects_v2)),
    [request],
  );

  return {
    loading,
    fetchSnapshots,
    fetchHeatmap,
    fetchClassSubjects,
    fetchClassGroupSummary,
    fetchSchoolWide,
    fetchStreamDrilldown,
    fetchStreamComparison,
    fetchTrend,
    fetchMultiPeriodTrend,
  };
};

export default useAnalysisSnapshots;
